import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface ChecklistItem {
  criteria: string;
  title: string;
  status: 'okay' | 'not-okay' | null;
  remarks: string;
}

export interface BakingSievesAndMagnetsCycleData {
  cycleNum: string;
  timeValue: string;
  executiveName: string;
  observedtime: string;
  items: ChecklistItem[];
}

interface OfflineSavedData {
  cycleNo: number;
  records: BakingSievesAndMagnetsCycleData[];
  timestamp: number;
}

interface BakingSievesAndMagnetsState {
  fetchedCycles: BakingSievesAndMagnetsCycleData[];
  offlineSavedData: OfflineSavedData[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastFetchTimestamp: number | null;
}

const initialState: BakingSievesAndMagnetsState = {
  fetchedCycles: [],
  offlineSavedData: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

const bakingSievesAndMagnetsSlice = createSlice({
  name: 'bakingSievesAndMagnets',
  initialState,
  reducers: {
    addOfflineData: (state, action: PayloadAction<OfflineSavedData>) => {
      state.offlineSavedData.push(action.payload);
    },
    clearOfflineData: (state) => {
      state.offlineSavedData = [];
    },
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        (item) => item.cycleNo !== action.payload
      );
    },
    setFetchedCycles: (state, action: PayloadAction<BakingSievesAndMagnetsCycleData[]>) => {
      state.fetchedCycles = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
  },
});

export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
} = bakingSievesAndMagnetsSlice.actions;

export const selectBakingSievesAndMagnetsState = (state: { bakingSievesAndMagnets: BakingSievesAndMagnetsState }) =>
  state.bakingSievesAndMagnets;
export const selectOfflineDataCount = (state: { bakingSievesAndMagnets: BakingSievesAndMagnetsState }) =>
  state.bakingSievesAndMagnets.offlineSavedData.length;
export const selectFetchedCycles = (state: { bakingSievesAndMagnets: BakingSievesAndMagnetsState }) =>
  state.bakingSievesAndMagnets.fetchedCycles;

export default bakingSievesAndMagnetsSlice.reducer;
